import { layoutToWorld } from '../../core/coords';
import { ensureCCW, ensureCW } from '../../geo/polygon';
import { triangulate } from '../../geo/triangulate';
import type { PolygonWithHoles, Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';

const FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };

/** A closed solid following a clipped deck outline and two height planes. */
export function buildBridgeSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  polygon: PolygonWithHoles,
  bottom: (p: Vec2) => number,
  top: (p: Vec2) => number
): void {
  const deck = { outer: ensureCCW(polygon.outer), holes: polygon.holes.map(ensureCW) };
  const mesh = triangulate(deck);
  const point = (i: number): Vec2 => [mesh.positions[i * 2]!, mesh.positions[i * 2 + 1]!];
  const world = (p: Vec2, height: (p: Vec2) => number): Vec3 => layoutToWorld(p, height(p));
  const uv = (p: Vec3): Vec2 => [p[0], -p[2]];
  for (let t = 0; t < mesh.indices.length; t += 3) {
    const triangle = [
      point(mesh.indices[t]!),
      point(mesh.indices[t + 1]!),
      point(mesh.indices[t + 2]!),
    ];
    const [a, b, c] = triangle as [Vec2, Vec2, Vec2];
    const area = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    if (Math.abs(area) < 1e-9) continue;
    const upward = area > 0 ? triangle : [...triangle].reverse();
    builder.polygon(
      style,
      FRAME,
      upward.map((p) => world(p, top)),
      uv
    );
    builder.polygon(
      style,
      FRAME,
      [...upward].reverse().map((p) => world(p, bottom)),
      uv
    );
  }
  for (const ring of [deck.outer, ...deck.holes]) {
    ring.forEach((p, i) => {
      const q = ring[(i + 1) % ring.length]!;
      builder.quad(style, FRAME, [
        world(p, bottom),
        world(q, bottom),
        world(q, top),
        world(p, top),
      ]);
    });
  }
}
